import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { products } from "../mockData";
import { ArrowRight, Package, CheckCircle2 } from "lucide-react";
import "swiper/css";
import "swiper/css/pagination";

const Products = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45 },
    },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgb(238,61,60), rgb(36,36,105))",
          }}
        />
        <div className="absolute inset-0 bg-black/10" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-28 md:py-36 text-center">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="mx-auto mb-6 w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-xl">
              <Package className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-white mb-5">
              Our Products
            </h1>

            <p className="text-lg md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              High-performance color dyes and specialty chemicals for textile,
              leather, paint & cosmetic industries.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 md:py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {(products || []).map((product) => {
              return (
                <motion.div
                  key={product.id}
                  variants={itemVariants}
                  className="group bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full"
                >
                  {/* Banner */}
                  <div className="relative h-60 overflow-hidden">
                    <Swiper
                      modules={[Autoplay, Pagination]}
                      slidesPerView={1}
                      loop={true}
                      autoplay={{
                        delay: 3000,
                        disableOnInteraction: false,
                      }}
                      pagination={{
                        clickable: true,
                        bulletClass: 'swiper-pagination-bullet',
                        bulletActiveClass: 'swiper-pagination-bullet-active',
                      }}
                      className="w-full h-full"
                      style={{
                        '--swiper-pagination-color': '#9333ea',
                        '--swiper-pagination-bullet-inactive-color': '#ffffff',
                        '--swiper-pagination-bullet-inactive-opacity': '0.5',
                        '--swiper-pagination-bullet-opacity': '1',
                        '--swiper-pagination-bullet-size': '8px',
                        '--swiper-pagination-bullet-horizontal-gap': '6px',
                      }}
                    >
                      {Array.isArray(product.image) ? product.image.map((img, idx) => (
                        <SwiperSlide key={idx}>
                          <img
                            src={img}
                            alt={`${product.title} ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </SwiperSlide>
                      )) : (
                        <SwiperSlide>
                          <img
                            src={product.image}
                            alt={product.title}
                            className="w-full h-full object-cover"
                          />
                        </SwiperSlide>
                      )}
                    </Swiper>

                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-white text-3xl font-bold">
                        {product.title}
                      </h3>

                      {product.subtitle && (
                        <p className="text-white/90 text-xs uppercase tracking-wide mt-1">
                          {product.subtitle}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    {/* Color Palette */}
                    {product.colorPalette && (
                      <div className="mb-5">
                        <p className="text-sm font-semibold text-gray-900 mb-3">
                          Shade Range
                        </p>

                        <div className="flex gap-2">
                          {product.colorPalette?.slice(0, 5).map((item, i) => (
                            <div key={i} className="text-center flex-1">
                              <div
                                className="w-8 h-8 rounded-lg border-2 border-white shadow mx-auto"
                                style={{ backgroundColor: item.color }}
                                title={item.name}
                              ></div>
                              <span className="text-[10px] text-gray-600 mt-1 block break-words leading-tight">
                                {item.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sub Types */}
                    {product.subTypes && (
                      <div className="mb-5">
                        <p className="text-sm font-semibold text-gray-900 mb-3">
                          Available Series
                        </p>

                        <div className="flex flex-wrap gap-2">
                          {product.subTypes.map((type, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200 hover:border-purple-300 hover:text-purple-700 transition"
                            >
                              {type.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-sm text-gray-600 leading-7 line-clamp-5 flex-grow">
                      {product.description}
                    </p>

                    {/* CTA */}
                    <Link
                      to={product.path}
                      className="mt-6 inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold"
                    >
                      View Details
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Industry Applications
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Specialized formulations designed for diverse industrial needs.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: "🧵",
                title: "Textile Industry",
                desc: "Premium dyes for cotton, silk, wool and synthetic fabrics.",
              },
              {
                icon: "👜",
                title: "Leather Industry",
                desc: "Specialized chemicals for leather finishing & treatment.",
              },
              {
                icon: "🎨",
                title: "Paint Industry",
                desc: "High-performance pigments and color additives.",
              },
              {
                icon: "💄",
                title: "Cosmetics",
                desc: "Safe, skin-friendly color solutions.",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all text-center"
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-r from-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-5">
              Need Custom Solutions?
            </h2>

            <p className="text-lg md:text-xl text-white/90 mb-8">
              We provide tailored formulations to match your exact industrial
              requirements.
            </p>

            <Link
              to="/contact"
              className="inline-block px-8 py-4 rounded-xl bg-white text-purple-700 font-semibold hover:scale-105 transition-all shadow-xl"
            >
              Contact Our Team
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products;
